import { useEffect, useState } from "react";
import { Delete, Keyboard, ArrowBigUp, CornerDownLeft } from "lucide-react";

const ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ñ"],
  ["z", "x", "c", "v", "b", "n", "m", ",", ".", "-"],
];
const NUM = [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["0"]];
const KEY_PREF = "touch_keyboard";

const setNativeValue = (el, value) => {
  const proto = el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
};

const isTextField = (el) => el && ((el.tagName === "INPUT" && !["checkbox", "radio", "number", "file", "range", "date"].includes(el.type)) || el.tagName === "TEXTAREA");

// Teclado en pantalla: escribe en el campo enfocado dentro de `scopeRef` (evita el teclado del sistema).
export const TouchKeyboard = ({ scopeRef, big = false, testid = "touch-keyboard" }) => {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY_PREF) !== "0");
  const [target, setTarget] = useState(null);
  const [caps, setCaps] = useState(true);

  useEffect(() => {
    const root = scopeRef?.current;
    if (!root) return undefined;
    const onFocus = (e) => {
      if (!isTextField(e.target)) return;
      if (enabled) e.target.setAttribute("inputmode", "none");
      else e.target.removeAttribute("inputmode");
      setTarget((prev) => { if (prev !== e.target) setCaps(!e.target.value && e.target.type !== "password" && !e.target.dataset.nocaps); return e.target; });
    };
    root.addEventListener("focusin", onFocus);
    // Campo con autoFocus: ya estaba enfocado antes de registrar el listener.
    const act = document.activeElement;
    if (act && root.contains(act)) onFocus({ target: act });
    return () => root.removeEventListener("focusin", onFocus);
  }, [scopeRef, enabled]);

  const toggle = () => {
    const v = !enabled;
    setEnabled(v);
    localStorage.setItem(KEY_PREF, v ? "1" : "0");
    if (target) { if (v) target.setAttribute("inputmode", "none"); else target.removeAttribute("inputmode"); target.blur(); target.focus(); }
  };

  const numeric = target && (target.type === "tel" || target.dataset.kb === "numeric");
  const type = (ch) => {
    if (!target) return;
    const v = target.value || "";
    const out = ch === "BACK" ? v.slice(0, -1) : v + (caps && /[a-zñ]/.test(ch) ? ch.toUpperCase() : ch);
    setNativeValue(target, out);
    const auto = target.type !== "password" && !target.dataset.nocaps;
    if (caps && /[a-zñ]/.test(ch)) setCaps(false);
    if (ch === " " && auto) setCaps(true);
    target.focus();
  };
  const next = () => {
    const root = scopeRef?.current;
    if (!root || !target) return;
    const fields = [...root.querySelectorAll("input, textarea")].filter((el) => isTextField(el) && !el.disabled && el.offsetParent);
    const i = fields.indexOf(target);
    if (fields[i + 1]) fields[i + 1].focus(); else target.blur();
  };

  const h = big ? "h-14 text-xl" : "h-11 text-base";
  const key = (k, extra = "") => (
    <button key={k} type="button" data-testid={`kb-key-${k === " " ? "space" : k}`} onMouseDown={(e) => e.preventDefault()} onClick={() => type(k)}
      className={`${h} rounded-lg bg-white border border-[#111827]/15 font-semibold text-[#1F2937] active:bg-[#E85D04] active:text-white transition-colors ${extra}`}>
      {caps && /[a-zñ]/.test(k) ? k.toUpperCase() : k}
    </button>
  );

  return (
    <div className="mt-2" data-testid={testid}>
      <button type="button" data-testid={`${testid}-toggle`} onMouseDown={(e) => e.preventDefault()} onClick={toggle}
        className="flex items-center gap-1.5 text-xs font-semibold text-[#111827]/50 hover:text-[#E85D04]">
        <Keyboard size={14} /> {enabled ? "Ocultar teclado en pantalla" : "Usar teclado en pantalla"}
      </button>
      {enabled && target && (
        <div className="mt-2 rounded-xl bg-[#F1F5F9] p-2 space-y-1.5 select-none" data-testid={`${testid}-pad`}>
          {numeric ? (
            <div className="grid grid-cols-3 gap-1.5 max-w-xs mx-auto">
              {NUM.flat().map((k) => key(k, k === "0" ? "col-span-2" : ""))}
              <button type="button" data-testid="kb-key-back" onMouseDown={(e) => e.preventDefault()} onClick={() => type("BACK")} className={`${h} rounded-lg bg-[#1F2937] text-white flex items-center justify-center`}><Delete size={18} /></button>
            </div>
          ) : (
            <>
              {ROWS.map((r, ri) => <div key={ri} className="grid grid-cols-10 gap-1.5">{r.map((k) => key(k))}</div>)}
              <div className="grid grid-cols-10 gap-1.5">
                <button type="button" data-testid="kb-key-shift" onMouseDown={(e) => e.preventDefault()} onClick={() => setCaps((c) => !c)}
                  className={`${h} col-span-2 rounded-lg border flex items-center justify-center ${caps ? "bg-[#E85D04] text-white border-[#E85D04]" : "bg-white border-[#111827]/15"}`}><ArrowBigUp size={18} /></button>
                {key("@", "")}
                {key(" ", "col-span-4")}
                <button type="button" data-testid="kb-key-back" onMouseDown={(e) => e.preventDefault()} onClick={() => type("BACK")} className={`${h} col-span-1 rounded-lg bg-[#1F2937] text-white flex items-center justify-center`}><Delete size={18} /></button>
                <button type="button" data-testid="kb-key-next" onMouseDown={(e) => e.preventDefault()} onClick={next} className={`${h} col-span-2 rounded-lg bg-[#16A34A] text-white flex items-center justify-center gap-1 text-sm font-bold`}><CornerDownLeft size={16} /> Sig.</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
